import React, { useState } from 'react';
import { Form, Formik } from 'formik';

import SectionCard from '@/components/common/SectionCard/SectionCard';
import Button, { ButtonStyleType } from '@/components/common/Button/Button';
import DatePickerTag4 from '@/components/common/DatePicker/DatePickerTag4';
import { STOCK_CODE_SEARCH_RESULTS_TO_SHOW } from '@/config/common.config';
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import Dropdown from '@/components/common/Dropdown/Dropdown';
import { SearchContracts } from '@/graphql/contracts/_generated_/searchContract.query';
import { useSearchContract } from '@/utils/data-helpers/contract';
import ContractDropdownHeaderItem from '@/components/common/ContractInputHeaderItem/ContractDropdownHeaderItem';
import useSearchCustomers from '@/pages/AddContract/hooks/useSearchCustomers';
import { validationSchema } from './validationSchema';
import { Checkbox } from '@/components/common/CheckboxTag4/CheckboxTag4';
import useGetProducts from '@/pages/ContractDetails/ContractLines/hooks/useGetProducts';
import { ProductOutputType } from '@/graphql/contracts/_generated_/contracts';

export type UpliftSearchProps = {
  searchString: string,
  dateFrom: Date | null,
  dateTo: Date | null,
  includeExemptFromUplift: boolean,
  includeDirectDebits: boolean,
  contractID: string,
  customerID: string,
};

interface IUpliftToolSearchProps {
  getSearchResults: (criteria: UpliftSearchProps) => void
}

type SearchTerm = null | boolean | undefined;

const checkBoxContainer = { style: { height: '22px', width: '22px' } };

export default function UpliftToolSearch({ getSearchResults }: IUpliftToolSearchProps) {
  const [includeExemptFromUplift, setIncludeExemptFromUplift] = useState<boolean>(false);
  const [includeDirectDebits, setIncludeDirectDebits] = useState<boolean>(true);

  const { searchResult: searchContractResult, isLoadingSearchContract, searchContract } = useSearchContract();
  const { getProducts, isLoadingGetProducts, products } = useGetProducts();
  const { isSearchLoading, searchCustomers, searchCustomersData } = useSearchCustomers();

  const haveSearchTerm = (
    dateFrom: SearchTerm,
    searchString: SearchTerm,
    contractID: SearchTerm,
    customerID: SearchTerm
  ) => {
    return dateFrom || searchString || contractID || customerID;
  };

  return (
        <>
            <Formik
                initialValues={{
                  dateFrom: null,
                  dateTo: null,
                  searchString: null,
                  contractID: null,
                  customerID: null
                }}
                validationSchema={validationSchema}
                onSubmit={(values) => {
                  if (haveSearchTerm(values.dateFrom, values.searchString, values.contractID, values.customerID)) {
                    getSearchResults({
                      dateFrom: values.dateFrom,
                      dateTo: values.dateTo,
                      searchString: values.searchString || '',
                      contractID: values.contractID || '',
                      customerID: values.customerID || '',
                      includeExemptFromUplift: includeExemptFromUplift,
                      includeDirectDebits: includeDirectDebits
                    } as UpliftSearchProps);
                  }
                }}
                validateOnMount={true}
            >
                {({ errors, setFieldValue, setFieldTouched, touched, values }) => (
                    <Form>

                        <SectionCard
                            padding={true}
                            title={{ titleText: 'Uplift Tool' }}
                            className="mb-20"
                        >
                            <div className="row ag-uplift-search">

                                <div className="col ag-uplift-search__col">
                                    <label
                                        className="d-block mb-10 fw-500"
                                        htmlFor="dateFrom"
                                    >
                                        Next Billing Date between:
                                    </label>

                                    <div className="row g-1">
                                        <div className="col">
                                            <DatePickerTag4
                                                id="dateFrom"
                                                placeholder="Start Date"
                                                value={values.dateFrom}
                                                onChange={({ value }) => {
                                                  setFieldTouched('dateFrom', true, false);
                                                  setFieldValue('dateFrom', value);
                                                }}
                                            />
                                            {errors.dateFrom && touched.dateFrom && (
                                                <ValidationAlert>{errors.dateFrom}</ValidationAlert>
                                            )}
                                        </div>
                                        <div className="col">
                                            <DatePickerTag4
                                                id="dateTo"
                                                placeholder="End Date"
                                                value={values.dateTo}
                                                onChange={({ value }) => {
                                                  setFieldTouched('dateTo', true, false);
                                                  setFieldValue('dateTo', value);
                                                }}
                                            />
                                            {errors.dateTo && touched.dateTo && (
                                                <ValidationAlert>{errors.dateTo}</ValidationAlert>
                                            )}
                                        </div>
                                    </div>

                                </div>

                                <div className="col ag-uplift-search__col">
                                    <label
                                        className="d-block mb-10 fw-500"
                                        htmlFor="product-search-input"
                                    >
                                        Search by Product:
                                    </label>
                                    <Dropdown<ProductOutputType>
                                        isSearchable={true}
                                        isClearable={true}
                                        optionsData={products}
                                        getOptionLabel={(option) => `${option?.stockCode} - ${option?.stockName}`}
                                        getOptionValue={(option: ProductOutputType) => option.stockCode!}
                                        placeholder="Stock Code or Name"
                                        id="product-search-input"
                                        isLoading={isLoadingGetProducts}
                                        selectedOption={
                                          products?.find(
                                            (el) => el?.stockCode === values.searchString
                                          ) ?? null
                                        }
                                        handleInputChange={(searchText) => getProducts({
                                          filter: {
                                            summary: searchText,
                                          },
                                          first: STOCK_CODE_SEARCH_RESULTS_TO_SHOW
                                        })}
                                        handleChange={(option) => {
                                          setFieldTouched('searchString', true, false);
                                          setFieldValue('searchString', option?.stockCode);
                                          setFieldValue('contractID', '');
                                        }}
                                        needToShowInputTextOnBlur={false}
                                    />

                                    {errors.searchString && touched.searchString && (
                                        <ValidationAlert>
                                            {errors.searchString}
                                        </ValidationAlert>
                                    )}
                                </div>

                                <div className="col ag-uplift-search__col">
                                    <label
                                        className="d-block mb-10 fw-500"
                                        htmlFor="contract-search-input"
                                    >
                                        Search by Contract Number:
                                    </label>
                                    <Dropdown<SearchContracts['searchContracts'][number]>
                                        isSearchable={true}
                                        isClearable={true}
                                        optionsData={searchContractResult}
                                        getOptionLabel={(option) =>
                                          `${option.contractNumber} - ${option.customer.customerName}`
                                        }
                                        getOptionValue={(option) => option.contractID}
                                        placeholder="Contract Number"
                                        id="contract-search-input"
                                        isLoading={isLoadingSearchContract}
                                        selectedOption={
                                          searchContractResult.find(
                                            (el) => el.contractID === values.contractID
                                          ) ?? null
                                        }
                                        handleInputChange={(value) => searchContract(value, 5)}
                                        handleChange={(option) => {
                                          setFieldTouched('contractID', true, false);
                                          setFieldValue('contractID', option?.contractID);
                                          setFieldValue('searchString', '');
                                        }}
                                        needToShowInputTextOnBlur={false}
                                    />

                                    {errors.searchString && touched.searchString && (
                                        <ValidationAlert >
                                            {errors.searchString}
                                        </ValidationAlert>
                                    )}
                                </div>
                                <div className="col ag-uplift-search__col">
                                    <ContractDropdownHeaderItem
                                        required={false}
                                        label="Search by Account:"
                                        id="customerId"
                                        placeholder="Account Name"
                                        getOptionLabel={(option) => option.customerName}
                                        getOptionValue={(option) => option.customerID}
                                        handleInputChange={(value) => searchCustomers(value)}
                                        optionsData={searchCustomersData || []}
                                        handleChange={(option) => {
                                          setFieldTouched('customerID');
                                          setFieldValue('customerID', option?.customerID ?? '');
                                        }}
                                        isLoading={isSearchLoading}
                                        isSearchable={true}
                                        isClearable={true}
                                        labelClassName="d-block mb-10 fw-500"
                                        needToShowInputTextOnBlur={false}
                                    />

                                    {errors.customerID && touched.customerID && (
                                        <ValidationAlert >
                                            {errors.customerID}
                                        </ValidationAlert>
                                    )}
                                </div>
                                <div className="col ag-uplift-search__checkbox-col">
                                    <div className="mb-20">
                                        <Checkbox
                                            id="include-exempt-checkbox"
                                            checked={includeExemptFromUplift}
                                            onChange={() => setIncludeExemptFromUplift(!includeExemptFromUplift)}
                                            containerProps={checkBoxContainer}>
                                            Include Exempt from Uplift
                                        </Checkbox>
                                    </div>

                                    <div>
                                        <Checkbox
                                            id="include-direct-debits"
                                            checked={includeDirectDebits}
                                            onChange={() => setIncludeDirectDebits(!includeDirectDebits)}
                                            containerProps={checkBoxContainer}>
                                            Include Direct Debits
                                        </Checkbox>
                                    </div>
                                </div>

                                <div className="pt-20 ag-uplift-search__search">
                                    <Button
                                        type="submit"
                                        buttonStyleType={ButtonStyleType.PRIMARY}
                                        disabled={!haveSearchTerm(values.dateFrom, values.searchString,
                                          values.contractID, values.customerID)}
                                    >
                                        Search
                                    </Button>
                                </div>

                            </div>
                        </SectionCard>
                    </Form>
                )}
            </Formik>
        </>
  );
}
